import styled from '@emotion/styled';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import Paper from '@base/Paper/Paper';
import H1 from '@base/H1/H1';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import breadcrumbs, { goBack } from '@utils/breadcrumbs';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import {
  emptyUpcomingStateConfig,
  UpcomingBatchEmptyState as EmptyState
} from '@comp/EmptyState/BatchGroupEmptyState';
import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import usePlatformGroupAnalytics from '@cont/BatchGroup/usePlatformGroupAnalytics';
import { getIsLoggedIn, isBatchSubscriptionActive } from '@selectors/auth';
import pushRoute from '@utils/pushRoute';
import { getGoalInfoByUIDFromData } from '@selectors/topologies';
import { emptyObject } from '@constants/empty';
import Dropdown from '@comp/Dropdown/Dropdown';
import useInfiniteList from '@hooks/useInfiniteList';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import useWindowSize from '@hooks/useWindowSize';
import {
  UPCOMING_SECTION_VIEWED,
  PLATFORM_PAGE_LAND,
  UPCOMING_BATCHES
} from '@cont/BatchGroup/constants/events';
import { useSWRConfig } from 'swr';
import useUpcomingBatches from '@cont/BatchGroup/useUpcomingBatches';
import { getBatchTypeByFilterOptions } from '@cont/BatchGroup/utils';
import { getIsSubscribedToOffline } from '@selectors/subscription';
import PlatformBatchCard from '../../Composite/BatchCard/PlatFormBatchCard';

const BatchListingContainer = styled.div`
  display: grid;
  grid-template-columns: ${({ $hasSideBar }) =>
    $hasSideBar ? 'repeat(18, 32px)' : 'unset'};
  grid-column-gap: ${({ $hasSideBar }) =>
    $hasSideBar ? `${Spacings.SPACING_4B}` : 'unset'};
  grid-column: ${({ $hasSideBar }) => ($hasSideBar ? 'span 18' : 'span 24')};
  padding-bottom: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoMobile} {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-bottom: ${Spacings.SPACING_0B};
  }
`;
const TopBar = styled(Paper)`
  display: grid;
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B} ${Spacings.SPACING_6B}
    ${Spacings.SPACING_12B};
  width: ${({ $hasSideBar }) => ($hasSideBar ? 'auto' : '1136px')};
  ${mediaQuery.uptoMobile} {
    width: auto;
    min-width: 330px;
    box-shadow: none;
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B};
    background-color: var(--color-base-0);
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const Title = styled(H1)`
  font-size: ${Fonts.H2};
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  margin-top: ${Spacings.SPACING_6B};
`;

const PlatformBatchCardContainer = styled(PlatformBatchCard)`
  width: ${({ $hasSideBar }) => ($hasSideBar ? '272px' : '365px')};
  margin-right: ${({ $hasSideBar }) =>
    $hasSideBar ? `${Spacings.SPACING_4B}` : `${Spacings.SPACING_5B}`};
  margin-bottom: ${Spacings.SPACING_8B};
  .batch-thumbnail {
    width: 100%;
    height: 190px;
  }
  ${mediaQuery.uptoMobile} {
    width: 330px;
    margin-bottom: ${Spacings.SPACING_6B};
    margin-right: ${Spacings.SPACING_0B};
    .batch-thumbnail {
      width: 100%;
      height: 150px;
    }
  }
`;
const FilterWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-column-gap: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_10B};
  & > button {
    box-shadow: none;
    white-space: nowrap;
    margin-top: ${Spacings.SPACING_0B};
    font-weight: ${Fonts.LIGHT};
    height: 45px;
  }
  ${mediaQuery.uptoMobile} {
    margin: ${Spacings.SPACING_0B} -${Spacings.SPACING_4B};
    grid-column: span 18;
    width: 100%;
    display: flex;
    padding: ${Spacings.SPACING_0B} ${Spacings.SPACING_2B};
    & > button {
      box-shadow: none;
      white-space: nowrap;
      margin-top: ${Spacings.SPACING_0B};
      width: 100%;
      font-size: ${Fonts.SMALL};
      max-width: 340px;
    }
  }
`;

const TopWrapper = styled.div`
  grid-column: span 18;
  display: grid;
  position: sticky;
  top: 72px;
  z-index: 1;
  background: var(--color-base-0);
  margin-bottom: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    position: relative;
    grid-column: span 10;
    grid-template-columns: repeat(10, 1fr);
    grid-column-gap: ${Spacings.SPACING_2B};
    top: ${Spacings.SPACING_0B};
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;
const ProgressWrapper = styled.div`
  grid-column: span 18;
  width: 484px;
  margin: auto;
  height: 120px;
  display: flex;
  justify-content: center;
  align-items: center;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
    min-height: 64vh;
  }
`;
const StyledDropdown = styled(Dropdown)`
  width: 176px;
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_6B};
  white-space: nowrap;
`;

const Loader = () => (
  <ProgressWrapper>
    <CircularLoader color="var(--color-i-green)" />
  </ProgressWrapper>
);

const renderBatchesWithSideBar = (batch, index) => {
  return (
    <PlatformBatchCardContainer
      isFeedCard
      batch={batch}
      key={batch.uid}
      dataAnalytics="batch"
      index={index}
      $hasSideBar
      lastPrimarySourceSection={UPCOMING_BATCHES}
    />
  );
};

const renderBatches = (batch, index) => {
  return (
    <PlatformBatchCardContainer
      batch={batch}
      key={batch.uid}
      dataAnalytics="batch"
      index={index}
      lastPrimarySourceSection={UPCOMING_BATCHES}
    />
  );
};

const getSelectedOption = (filterOptions = [], value) => {
  const selectedOption = filterOptions?.filter(
    (option) => option.value === parseInt(value, 10)
  );
  return selectedOption?.[0] || { label: 'All batches', value: 0 };
};

const BatchesGroupListing = ({
  hasSideBar,
  filterOptions,
  queryType,
  name
}) => {
  const router = useRouter();
  const { goalUID, goalSlug } = router.query;
  const { cache } = useSWRConfig();
  const sendAnalyticsToSegment = usePlatformGroupAnalytics(goalUID);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isSubscriptionActive = useSelector((state) =>
    isBatchSubscriptionActive(state, goalUID)
  );
  const { isMobile } = useWindowSize();
  const refetchingIntervalRef = useRef(null);
  const filterWrapperRef = useRef('');
  const [appliedFilter, setFiltersApplied] = useState(() =>
    getSelectedOption(filterOptions, queryType)
  );
  const { setSize, isValidating, batchesByFilter } = useUpcomingBatches({
    goalUID,
    type: appliedFilter.value
  });

  const [showRouteChangeLoader, setShowRouteChangeLoader] = useState(false);

  useEffect(() => {
    sendAnalyticsToSegment(PLATFORM_PAGE_LAND, {
      last_primary_source_section: UPCOMING_BATCHES
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      // Clear timeout to avoid memory leaks.
      if (refetchingIntervalRef.current)
        refetchingIntervalRef.current = clearInterval(
          refetchingIntervalRef.current
        );
    };
  }, []);

  useEffect(() => {
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/upcoming-batches?type=${appliedFilter.value}`,
      `/goal/${goalSlug}/${goalUID}/upcoming-batches?type=${appliedFilter.value}`
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, isSubscriptionActive]);

  useLayoutEffect(() => {
    function stickyFilter() {
      if (window.pageYOffset > 106) {
        filterWrapperRef.current.classList.add('sticky');
      } else {
        filterWrapperRef.current.classList.remove('sticky');
      }
    }
    window.addEventListener('scroll', stickyFilter);
    return () => {
      window.removeEventListener('scroll', stickyFilter);
    };
  }, []);

  const loadMoreCourses = () => {
    if (!isValidating && batchesByFilter?.next)
      return new Promise((resolve) => {
        setSize((nPages) => nPages + 1);
        refetchingIntervalRef.current = setTimeout(() => resolve(), 700);
      }, []);
  };
  const selectFilter = async (option) => {
    setShowRouteChangeLoader(true);
    setSize(1);
    cache.clear();
    const queryParams = option.value;
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/upcoming-batches?type=${queryParams}`,
      `/goal/${goalSlug}/${goalUID}/upcoming-batches?type=${queryParams}`,
      { shallow: true }
    );
    setFiltersApplied(option);
    setShowRouteChangeLoader(false);
    const batchType = getBatchTypeByFilterOptions(option);
    sendAnalyticsToSegment(UPCOMING_SECTION_VIEWED, {
      batch_type: batchType,
      last_primary_source_section: UPCOMING_BATCHES
    });
  };

  const goalDetails =
    useSelector((state) => getGoalInfoByUIDFromData(state, goalUID)) ||
    emptyObject;
  const count = batchesByFilter?.count ?? 0;
  const topBlockLabel = `${name} ${count === 1 ? 'batch' : 'batches'}`;
  const FilteredBatchDataToRender = useInfiniteList({
    loadMore: loadMoreCourses,
    data: batchesByFilter,
    renderItem: hasSideBar ? renderBatchesWithSideBar : renderBatches,
    Loader
  });

  const isFilterSelected = () => {
    const { type } = router.query;
    return appliedFilter.value === parseInt(type, 10);
  };
  const isOfflineSubscription = useSelector(getIsSubscribedToOffline(goalUID));
  return (
    <BatchListingContainer $hasSideBar={hasSideBar}>
      <TopWrapper>
        <TopBar gridColumn="span 18" $hasSideBar={hasSideBar}>
          <BreadCrumbs
            tags={breadcrumbs.getSeeAllTags(
              'enrolledBatches',
              goalDetails,
              null,
              null,
              null,
              null,
              isOfflineSubscription
            )}
            onBackClick={goBack}
          />
          <Title gridColumn="span 18">{topBlockLabel}</Title>
        </TopBar>
      </TopWrapper>
      <FilterWrapper ref={filterWrapperRef}>
        <StyledDropdown
          options={filterOptions || []}
          selectedOption={appliedFilter}
          onOptionClick={selectFilter}
          stripLength={isMobile ? 120 : 16}
          menuWidth={isMobile ? '100%' : '176px'}
          showSelectedBorder={isFilterSelected}
        />
      </FilterWrapper>
      {showRouteChangeLoader ? <Loader /> : FilteredBatchDataToRender}
      {!batchesByFilter?.isFetching &&
        batchesByFilter?.results?.length === 0 && (
          <EmptyState config={emptyUpcomingStateConfig} />
        )}
    </BatchListingContainer>
  );
};
export default BatchesGroupListing;
